// Client-side image compression — required because Cloudinary's free
// tier rejects single uploads >10 MB. DSLR transfers from Photos roll
// commonly hit 12–40 MB, so we resize before signing the upload.
//
// Approach: resize to 4096 px on the long edge at JPEG quality 90.
// That's ~6 MP — comfortably below 10 MB for Canon 6D Mark II / Sony
// A7 IV captures, and high enough resolution that the Claude verifier
// has plenty of detail. EXIF metadata is preserved through the
// browser-image-compression `preserveExif` flag so the spectator
// dashboard's camera-caption + GPS pipeline keeps working.
//
// Skip-conditions (return the original File untouched):
//   - file is missing or not an image
//   - file is already under 2 MB (no compression worth doing)
//   - file is HEIC/HEIF (Safari can decode in-browser to <canvas> but
//     compression libs occasionally choke; let Cloudinary's server-side
//     conversion handle these instead)

import imageCompression from 'browser-image-compression';

const SKIP_BELOW_BYTES = 2 * 1024 * 1024; // 2 MB
const MAX_LONG_EDGE = 4096;
const QUALITY = 0.9;

const HEIC_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

export function shouldCompress(file) {
  if (!file?.type || !file.type.startsWith('image/')) return false;
  if (HEIC_TYPES.has(file.type.toLowerCase())) return false;
  if (file.size < SKIP_BELOW_BYTES) return false;
  return true;
}

// Compress a single File. Returns a new File on success; on any error
// returns the original (the upload pipeline still tries — Cloudinary
// will surface the size limit as an actionable error if compression
// itself failed).
export async function compressForUpload(file, { onStart, onDone } = {}) {
  if (!shouldCompress(file)) return file;
  try {
    onStart?.();
    const out = await imageCompression(file, {
      maxSizeMB: 9.5, // soft cap: stay under Cloudinary free 10 MB hard limit
      maxWidthOrHeight: MAX_LONG_EDGE,
      useWebWorker: true,
      preserveExif: true, // critical — drives camera_caption + EXIF GPS
      fileType: 'image/jpeg',
      initialQuality: QUALITY,
    });
    onDone?.({ before: file.size, after: out.size });
    // browser-image-compression returns a Blob without a `name`; coerce
    // back to File so the existing pipeline (which reads file.name)
    // doesn't lose the original filename.
    if (out instanceof File) return out;
    return new File([out], file.name || 'photo.jpg', {
      type: 'image/jpeg',
      lastModified: file.lastModified || Date.now(),
    });
  } catch (err) {
    console.warn('[image-compression] failed, falling back to original:', err);
    onDone?.({ before: file.size, after: file.size, error: err.message });
    return file;
  }
}

// Compress every file in an array. Sequential (not parallel) so we
// don't spike memory on iOS Safari with multiple 30 MB JPEGs decoding
// simultaneously.
export async function compressBatch(files, { onProgress } = {}) {
  const out = [];
  for (let i = 0; i < files.length; i += 1) {
    onProgress?.({ index: i, total: files.length, file: files[i] });
    out.push(await compressForUpload(files[i]));
  }
  return out;
}
